import React, { useEffect, useState } from 'react';

const ContactDetails = ({ formData, setFormData, errors = {}, setErrors, isLocked = false }) => {
  const [pincodeError, setPincodeError] = useState('');

  const fetchPincodeDetails = async (pincode) => {
    try {
      setPincodeError('');
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();

      if (data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
        const { District, State } = data[0].PostOffice[0];
        setFormData(prevData => ({
          ...prevData,
          address: {
            ...prevData.address,
            district: District,
            state: State,
            pincode: pincode // Preserve the pincode
          }
        }));
        if (setErrors) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next['address.district'];
            delete next['address.state'];
            delete next['address.pincode'];
            return next;
          });
        }
      } else {
        setPincodeError('Invalid pincode');
      }
    } catch (error) {
      setPincodeError('Error fetching pincode details');
    }
  };

  const handleChange = (e) => {
    if (isLocked) return;
    const { name, value } = e.target;
    const clearIfValid = (fieldName, fieldValue, opts = {}) => {
      if (!setErrors) return;
      const valid = opts.strictValid !== undefined ? opts.strictValid : (fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '');
      if (valid) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[fieldName];
          return next;
        });
      }
    };
    if (name.includes('.')) {
      const [parent, child] = name.split('.');

      // Special handling for pincode
      if (child === 'pincode') {
        // Only allow numbers and limit to 6 digits
        const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
        setFormData({
          ...formData,
          [parent]: {
            ...formData[parent],
            [child]: numericValue
          }
        });

        // If pincode is 6 digits, fetch details
        if (numericValue.length === 6) {
          fetchPincodeDetails(numericValue);
          clearIfValid('address.pincode', numericValue, { strictValid: true });
        }
        return;
      }

      setFormData({
        ...formData,
        [parent]: {
          ...(formData[parent] || {}),
          [child]: value
        }
      });
      clearIfValid(name, value);
    } else {
      // Special handling for mobile: allow only numbers and limit to 10 digits
      if (name === 'mobile') {
        const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
        setFormData({
          ...formData,
          [name]: numericValue
        });
        clearIfValid(name, numericValue);
      } else {
        setFormData({
          ...formData,
          [name]: value
        });
        clearIfValid(name, value);
      }
    }
  };

  const getErrorClass = (fieldName) => {
    return errors[fieldName]
      ? "mt-1 block w-full rounded-md border border-red-300 shadow-sm focus:border focus:border-red-500 focus:ring-red-500 px-4 py-2.5 text-black"
      : `mt-1 block w-full rounded-md border ${isLocked ? 'border-[#cccccc]' : 'border-[#753ABF]'} shadow-sm focus:border ${isLocked ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} focus:ring-[#753ABF] px-4 py-2.5 text-black ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`;
  };

  // Mobile should follow the same editing rule as Email: always non-editable in this section

  return (
    <div className="space-y-12">
      <div className="space-y-12">
        <div>
            <label className="block text-sm font-bold text-black">
              Phone no.<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="mobile"
                id="contact_mobile"
                value={formData.mobile || ''}
                onChange={handleChange}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="10"
                disabled={isLocked}
                className={getErrorClass('mobile')}
                placeholder="Type your phone number here"
              />
            </div>
            {errors.mobile && (
              <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-bold text-black">
              Email<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="email"
                id="contact_email"
                value={formData.email || ''}
                onChange={handleChange}
                disabled
                className={`mt-1 block w-full rounded-md shadow-sm focus:border ${isLocked ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} focus:ring-[#753ABF] px-4 py-2.5 pr-10 ${errors.email ? 'border-red-500' : `border ${isLocked ? 'border-[#cccccc]' : 'border-[#753ABF]'}`
                  } bg-[#eeecec] text-[#666666]`}
                placeholder="Type your email address here"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>
      </div>
      <div className="space-y-8">
        <h3 className="text-lg font-medium text-[#753ABF]">Mailing Address</h3>
        <div>
          <label className="block text-sm font-bold text-black">
            Pincode<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="6"
            name="address.pincode"
            id="address_pincode"
            value={formData.address?.pincode || ''}
            onChange={handleChange}
            disabled={isLocked}
            className={getErrorClass('address.pincode')}
            placeholder="Enter 6-digit pincode"
          />
          {errors['address.pincode'] && (
            <p className="mt-1 text-sm text-red-600">{errors['address.pincode']}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-black">
              District<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address.district"
              id="address_district"
              value={formData.address?.district || ''}
              onChange={handleChange}
              className={getErrorClass('address.district')}
              placeholder="District will be auto-filled"
              disabled
                style={{ backgroundColor: '#eeecec' }}
              />
            {errors['address.district'] && (
              <p className="mt-1 text-sm text-red-600">{errors['address.district']}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-black">
              City<span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address.city"
              id="address_city"
              value={formData.address?.city || ''}
              onChange={handleChange}
              disabled={isLocked}
              className={getErrorClass('address.city')}
              placeholder="Enter city name"
            />
            {errors['address.city'] && (
              <p className="mt-1 text-sm text-red-600">{errors['address.city']}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-bold text-black">
            State/UT<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="address.state"
            id="address_state"
            value={formData.address?.state || ''}
            onChange={handleChange}
            className={getErrorClass('address.state')}
            placeholder="State will be auto-filled"
            disabled
              style={{ backgroundColor: '#eeecec' }}
          />
          {errors['address.state'] && (
            <p className="mt-1 text-sm text-red-600">{errors['address.state']}</p>
          )}
          {pincodeError && (
            <p className="mt-1 text-sm text-red-600">{pincodeError}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-black">
            Address<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="address.line_1"
            id="address_line_1"
            value={formData.address?.line_1 || ''}
            onChange={handleChange}
            disabled={isLocked}
            className={getErrorClass('address.line_1')}
            placeholder="Enter House no, Building Name, Block etc."
          />
          {errors['address.line_1'] && (
            <p className="mt-1 text-sm text-red-600">{errors['address.line_1']}</p>
          )}
        </div>

        <div>
          <input
            type="text"
            name="address.line_2"
            value={formData.address?.line_2 || ''}
            onChange={handleChange}
            disabled={isLocked}
            className={`mt-1 block w-full rounded-md border ${isLocked ? 'border-[#cccccc]' : 'border-[#753ABF]'} shadow-sm focus:border ${isLocked ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} focus:ring-[#753ABF] px-4 py-2.5 ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder="Enter Landmark (optional)"
          />
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;
